import type { ReactNode } from 'react';
import {
  coreExtensionData,
  createFrontendModule,
  featureFlagsApiRef,
  NotFoundErrorPage,
  useApi,
} from '@backstage/frontend-plugin-api';
import mcpChatPlugin from '@backstage-community/plugin-mcp-chat/alpha';

// PAT-1026: `/mcp-chat` is the page of the yarn-patched
// `@backstage-community/plugin-mcp-chat/alpha`. The patch edits
// `dist/components/ChatContainer/QuickStart.esm.js`, which the `/alpha` page
// loader reaches through the same `ChatPage` module as the legacy entry point,
// so it still applies.
//
// The plugin declares no feature flag and has no gate of its own. This
// override keeps the extension id `page:mcp-chat`, its path, route ref, title
// and icon, and:
// - turns the framework header off: `ChatPage` is a full-height page with its
//   own layout, and the legacy-converted page had no header either;
// - renders the app's not-found page unless the app-level `mcp-chat` flag
//   (declared in `appFeatureFlags` in `App.tsx`) is on, like the legacy
//   `RequireFeatureFlag` route did.
export const MCP_CHAT_FLAG = 'mcp-chat';

const McpChatFlagGate = ({ children }: { children: ReactNode }) => {
  const featureFlagsApi = useApi(featureFlagsApiRef);
  if (!featureFlagsApi.isActive(MCP_CHAT_FLAG)) {
    return <NotFoundErrorPage />;
  }
  return <>{children}</>;
};

const mcpChatPageOverride = mcpChatPlugin
  .getExtension('page:mcp-chat')
  .override({
    *factory(originalFactory) {
      // `override()` takes either `params` or `factory`, so the params go
      // through the original factory (merged into the plugin's own).
      const original = originalFactory({ params: { noHeader: true } });
      yield* original;
      // Same id as the original element, so it replaces it.
      yield coreExtensionData.reactElement(
        <McpChatFlagGate>
          {original.get(coreExtensionData.reactElement)}
        </McpChatFlagGate>,
      );
    },
  });

export const mcpChatModule = createFrontendModule({
  pluginId: 'mcp-chat',
  extensions: [mcpChatPageOverride],
});
